import { Box, ThemeProvider, Typography } from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import {
  darkTheme,
  lightTheme,
} from 'components/DatePIcker/MuiDatePickerTheme';
import dayjs, { type Dayjs } from 'dayjs';
import useAppTheme from 'hooks/useAppTheme';
import useTimeDisplayMode from 'hooks/useTimeDisplayMode';
import { useMemo } from 'react';
import { DATE_TIME_FORMAT } from 'utils/constants/dateTime';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

export const DateSelectorMui = ({
  startDateTimeUTC,
  endDateTimeUTC,
  handleStartDateTimeChange,
  handleEndDateTimeChange,
  openEndDate,
  setOpenEndDate,
  format = DATE_TIME_FORMAT,
  startMinDate,
  startMinTime,
  startMaxDate,
  startMaxTime,
  endMinDate,
  endMinTime,
  endMaxDate,
  endMaxTime,
  endMaxDateTime,
  endMinDateTime,
}: {
  startDateTimeUTC: Dayjs;
  endDateTimeUTC: Dayjs;
  handleStartDateTimeChange: any;
  handleEndDateTimeChange: any;
  openEndDate?: boolean;
  setOpenEndDate?: any;
  format?: string;
  startMinDate?: Dayjs;
  startMinTime?: Dayjs;
  startMaxDate?: Dayjs;
  startMaxTime?: Dayjs;
  endMinDate?: Dayjs;
  endMinTime?: Dayjs;
  endMaxDate?: Dayjs;
  endMaxTime?: Dayjs;
  endMaxDateTime?: Dayjs;
  endMinDateTime?: Dayjs;
}) => {
  const { t } = useTranslation();
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const { preferredTimeDisplayMode, timezone } = useTimeDisplayMode();

  const displayTimezone = useMemo(() => {
    switch (preferredTimeDisplayMode) {
      case 'utc':
        return 'UTC';
      case 'local':
        return dayjs.tz.guess();
      case 'configured':
        return timezone || dayjs.tz.guess(); // fallback to get local incase api call is in progress
      default:
        return 'UTC';
    }
  }, [preferredTimeDisplayMode, timezone]);

  const datePickerSx = {
    '& .MuiInputBase-root': {
      backgroundColor: enableDarkTheme
        ? palette.toolBarBackground
        : palette.menuOptions,
      border: `1px solid ${
        enableDarkTheme ? palette.borderGrey : palette.border
      }`,
      borderRadius: '8px',
      height: '32px',
      fontSize: '14px',
      transition: 'all 0.2s ease',
      '&:hover': {
        borderColor: palette.primary,
        backgroundColor: enableDarkTheme
          ? palette.boxBackground
          : palette.sectionBackground,
      },
      '&.Mui-focused': {
        borderColor: palette.primary,
        backgroundColor: enableDarkTheme
          ? palette.boxBackground
          : palette.menuOptions,
        boxShadow: `0 0 0 2px ${palette.primary}4d`,
      },
    },
    '& .MuiInputBase-input': {
      color: palette.textPrimary,
      fontSize: '14px',
      padding: '8px 12px',
      fontWeight: 400,
    },
    '& .MuiIconButton-root': {
      color: palette.textSecondary,
      padding: '4px',
      '&:hover': {
        backgroundColor: enableDarkTheme
          ? `${palette.primary}1a`
          : 'rgba(0, 0, 0, 0.04)',
      },
    },
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    minWidth: '200px',
    maxWidth: '200px',
    width: '200px',
  };

  return (
    <ThemeProvider theme={enableDarkTheme ? darkTheme : lightTheme}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography
              variant="body2"
              sx={{
                fontSize: '10px',
                color: palette.textPrimary,
                fontWeight: 500,
                minWidth: 'fit-content',
              }}
            >
              {t('from')}
            </Typography>
            <DateTimePicker
              timezone={displayTimezone}
              value={startDateTimeUTC}
              onChange={handleStartDateTimeChange}
              disableFuture
              format={format}
              timeSteps={{ minutes: 1 }}
              onClose={
                typeof openEndDate === 'boolean'
                  ? () => setOpenEndDate(true)
                  : undefined
              }
              onAccept={
                typeof openEndDate === 'boolean'
                  ? () => setOpenEndDate(true)
                  : undefined
              }
              sx={datePickerSx}
              slotProps={{
                textField: { size: 'small' },
                actionBar: {
                  actions: [],
                },
                digitalClockSectionItem: {
                  style: {
                    padding: '6px',
                  },
                },
                day: {
                  style: {
                    fontSize: '14px',
                  },
                },
              }}
              minDate={startMinDate}
              maxDate={startMaxDate}
              minTime={startMinTime}
              maxTime={startMaxTime}
              ampm={false}
            />
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography
              variant="body2"
              sx={{
                fontSize: '10px',
                color: palette.textPrimary,
                fontWeight: 500,
                minWidth: 'fit-content',
              }}
            >
              {t('to')}
            </Typography>
            <DateTimePicker
              timezone={displayTimezone}
              value={endDateTimeUTC}
              onChange={handleEndDateTimeChange}
              format={format}
              timeSteps={{ minutes: 1 }}
              open={typeof openEndDate === 'boolean' ? openEndDate : undefined}
              onClose={() => {
                setOpenEndDate?.(false);
              }}
              onOpen={
                typeof openEndDate === 'boolean'
                  ? () => setOpenEndDate?.(true)
                  : undefined
              }
              sx={datePickerSx}
              slotProps={{
                textField: { size: 'small' },
                actionBar: {
                  actions: [],
                },
                digitalClockSectionItem: {
                  style: {
                    padding: '6px',
                  },
                },
                day: {
                  style: {
                    fontSize: '14px',
                  },
                },
              }}
              minDate={endMinDateTime ? undefined : endMinDate}
              maxDate={endMaxDateTime ? undefined : endMaxDate}
              minTime={endMinDateTime ? undefined : endMinTime}
              maxTime={endMaxDateTime ? undefined : endMaxTime}
              minDateTime={
                endMinDateTime ?? endMinDate ?? endMinTime ?? startDateTimeUTC
              }
              maxDateTime={
                endMaxDateTime ??
                endMaxDate ??
                endMaxTime ??
                startDateTimeUTC?.add(15, 'minute')
              }
              ampm={false}
            />
          </Box>
        </Box>
      </LocalizationProvider>
    </ThemeProvider>
  );
};
